import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Icon from '../../components/AppIcon';
import { logout, selectCurrentUser } from '../../features/auth/authSlice';
import { isProfessionalRole } from '../../lib/authPortals';
import CurrencySwitcher from '../../components/ui/CurrencySwitcher';
import NotificationBell from '../../components/ui/NotificationBell';
import AgentLocationBanner from '../../components/ui/AgentLocationBanner';

const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [
      { to: '/agent-dashboard', label: 'Dashboard', icon: 'LayoutDashboard', exact: true },
      { to: '/agent-dashboard/notifications', label: 'Notifications', icon: 'Bell' },
      { to: '/agent-dashboard/properties', label: 'My Properties', icon: 'Building2' },
      { to: '/agent-dashboard/properties/new', label: 'Add Property', icon: 'PlusCircle' },
    ],
  },
  {
    label: 'Collaboration',
    items: [
      { to: '/workspace/co-broker', label: 'Co-Brokering', icon: 'Users' },
      { to: '/workspace/crm', label: 'CRM & Leads', icon: 'Contact' },
      { to: '/notice-board', label: 'Notice Board', icon: 'ClipboardList' },
    ],
  },
  {
    label: 'Client services',
    items: [
      { to: '/workspace/bookings', label: 'Advisory Bookings', icon: 'Calendar' },
      { to: '/workspace/documents', label: 'E-Sign Documents', icon: 'FileText' },
    ],
  },
  {
    label: 'Account',
    items: [
      { to: '/agent-dashboard/subscription', label: 'Subscription', icon: 'Crown' },
      { to: '/agent-dashboard/promote', label: 'Promote', icon: 'Megaphone' },
      { to: '/agent-dashboard/profile', label: 'Profile', icon: 'User' },
      { to: '/agent-dashboard/verification', label: 'Verification', icon: 'ShieldCheck' },
      { to: '/property-search-browse', label: 'Browse market', icon: 'Search' },
    ],
  },
];

const AgentLayout = ({ children, title, subtitle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAgent = isProfessionalRole(user?.account_role);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">
        <div className="text-center space-y-3">
          <p>Please sign in as a Real Estate Agent.</p>
          <button
            type="button"
            className="px-4 py-2 rounded-lg bg-amber-500 text-slate-950 font-semibold"
            onClick={() => navigate('/pro/login')}
          >
            Agent login
          </button>
        </div>
      </div>
    );
  }

  if (!isAgent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white px-4">
        <div className="text-center space-y-4 max-w-md">
          <Icon name="Briefcase" size={40} className="mx-auto text-amber-400" />
          <h1 className="text-2xl font-semibold">Agent dashboard only</h1>
          <p className="text-slate-400 text-sm">
            This professional console is for agents, brokers, and advisors. Switch to a Real Estate
            Agent account or continue as a user.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <button
              type="button"
              className="px-4 py-2 rounded-lg bg-amber-500 text-slate-950 font-semibold"
              onClick={() => navigate('/pro/register')}
            >
              Become an agent
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded-lg border border-slate-600"
              onClick={() => navigate('/user-dashboard')}
            >
              User dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const Sidebar = ({ onNavigate }) => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-6 border-b border-slate-800">
        <Link to="/" className="flex items-center gap-2 mb-4">
          <img src="/assets/images/homelogo.png" alt="HireBroker" className="h-8" />
        </Link>
        <p className="text-[10px] uppercase tracking-[0.2em] text-amber-500/90 font-semibold">
          Agent console
        </p>
        <p className="text-white font-semibold mt-1 truncate">{user?.name || 'Agent'}</p>
        <p className="text-xs text-slate-400 capitalize mt-0.5">
          {user?.account_role || 'agent'}
        </p>
        <div className="mt-2">
          {user?.verification_status === 'verified' ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
              <Icon name="BadgeCheck" size={12} />
              Verified agent
            </span>
          ) : (
            <Link
              to="/agent-dashboard/verification"
              onClick={onNavigate}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-500/15 text-amber-400 border border-amber-500/30 hover:bg-amber-500/25"
            >
              <Icon name="ShieldAlert" size={12} />
              Get verified
            </Link>
          )}
        </div>
        {user?.company_name && (
          <p className="text-xs text-slate-500 mt-2 truncate">{user.company_name}</p>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {NAV_GROUPS.map((group) => (
          <div key={group.label}>
            <p className="px-3 mb-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              {group.label}
            </p>
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const active = item.exact
                  ? location.pathname === item.to
                  : location.pathname === item.to ||
                    (item.to !== '/agent-dashboard' &&
                      location.pathname.startsWith(item.to));
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      onClick={onNavigate}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        active
                          ? 'bg-amber-500/15 text-amber-400 font-medium'
                          : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                      }`}
                    >
                      <Icon name={item.icon} size={18} />
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="p-4 border-t border-slate-800 space-y-2">
        <Link
          to="/workspace"
          onClick={onNavigate}
          className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white"
        >
          <Icon name="Layers" size={16} />
          Collaboration workspace
        </Link>
        <button
          type="button"
          onClick={() => {
            dispatch(logout());
            navigate('/pro/login');
          }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-red-400"
        >
          <Icon name="LogOut" size={16} />
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-72 flex-col bg-slate-950 text-white fixed inset-y-0 left-0 z-30">
        <Sidebar />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-slate-950 text-white shadow-xl">
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:pl-72 min-h-screen flex flex-col">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200 px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              className="lg:hidden p-2 rounded-lg border border-slate-200"
              onClick={() => setMobileOpen(true)}
            >
              <Icon name="Menu" size={20} />
            </button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-semibold text-slate-900 truncate">{title}</h1>
              {subtitle && (
                <p className="text-xs sm:text-sm text-slate-500 truncate">{subtitle}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <CurrencySwitcher compact />
            <NotificationBell accent="amber" inboxPath="/agent-dashboard/notifications" />
            {user?.verification_status === 'verified' ? (
              <span
                className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200"
                title="Verified Real Estate Agent"
              >
                <Icon name="BadgeCheck" size={14} />
                Verified
              </span>
            ) : (
              <Link
                to="/agent-dashboard/verification"
                className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100"
                title="Complete verification to earn your trust badge"
              >
                <Icon name="ShieldAlert" size={14} />
                Unverified
              </Link>
            )}
            <Link
              to="/agent-dashboard/properties/new"
              className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
            >
              <Icon name="Plus" size={16} />
              Add Property
            </Link>
            <Link
              to="/agent-dashboard/profile"
              className="w-9 h-9 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center text-sm font-bold hover:ring-2 hover:ring-amber-300"
              title="My profile"
            >
              {(user?.name || 'A').charAt(0).toUpperCase()}
            </Link>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-8">
          <AgentLocationBanner profilePath="/agent-dashboard/profile" />
          {children}
        </main>
      </div>
    </div>
  );
};

export default AgentLayout;
